// src/pages/returnToVendor/ReturnToVendorListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RotateCcw, Plus, Eye, Calendar, Clock, FileText, PackageCheck, User, CheckCircle, XCircle } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getReturnsToVendor } from '../../services/api/procurement/returnToVendor';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { PURCHASE_ORDER_BASE_PATH } from '../../utils/procurementNavigation';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './ReturnToVendorListPage.module.css';

// Server-side filters: `search` (RTV, PO or GRN number) and `status`
// (rettzy-backend .../procurement/services/returnToVendor.service.js).
const STATUS_ICONS = { completed: CheckCircle, voided: XCircle };

const statusConfig = {
  completed: { color: '#10b981', bg: '#d1fae5' },
  voided: { color: '#ef4444', bg: '#fee2e2' },
};

const ReturnToVendorListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [returnsToVendor, setReturnsToVendor] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '' });

  useEffect(() => {
    fetchReturnsToVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchReturnsToVendor = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      if (filters.search) params.search = filters.search.trim();
      if (filters.status) params.status = filters.status;

      const response = await getReturnsToVendor(params);
      setReturnsToVendor(response.returnsToVendor || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch returns to vendor:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('returnToVendor.failedToLoadReturnToVendors'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', status: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some(v => v !== '');

  const filterDefinitions = [
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: t('returnToVendor.allStatuses'),
      options: Object.keys(statusConfig).map(status => ({
        value: status,
        label: t(`returnToVendor.status_${status}`),
      })),
    },
  ];

  const columns = [
    {
      key: 'rtvNumber',
      header: t('returnToVendor.rtvNumber'),
      width: '170px',
      render: (row) => <DocNumberCell number={row.rtvNumber} to={`/procurement/return-to-vendor/${row.id}`} />,
    },
    {
      key: 'purchaseOrder',
      header: t('returnToVendor.purchaseOrderNumber'),
      width: '160px',
      render: (row) => row.purchaseOrder?.purchaseOrderNumber ? (
        <Link className={tb.docLink} to={`${PURCHASE_ORDER_BASE_PATH}/${row.purchaseOrder.id}`} onClick={(e) => e.stopPropagation()}>
          <FileText size={12} />{row.purchaseOrder.purchaseOrderNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'supplier',
      header: t('returnToVendor.supplier'),
      render: (row) => <div className={tb.partyInfo}><div className={tb.partyName}><User size={14} />{(row.supplier?.name || row.Supplier?.name) || t('common.na')}</div></div>,
    },
    {
      key: 'goodsReceivedNote',
      header: t('returnToVendor.grnNumber'),
      width: '150px',
      render: (row) => row.goodsReceivedNote?.grnNumber ? (
        <Link className={tb.docLink} to={`/procurement/grn/${row.goodsReceivedNote.id}`} onClick={(e) => e.stopPropagation()}>
          <PackageCheck size={12} />{row.goodsReceivedNote.grnNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'totalDebitAmount',
      header: t('returnToVendor.totalDebitAmount'),
      width: '150px',
      align: 'right',
      render: (row) => <div className={tb.totalAmount}>{formatCurrency(row.totalDebitAmount)}</div>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.completed;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}>
            <StatusIcon size={14} />
            <span>{t(`returnToVendor.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/procurement/return-to-vendor/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.returnToVendorListPage}>
      <PageHeader
        title={t('returnToVendor.allReturnToVendors')}
        description={t('returnToVendor.viewAndManageAllReturnToVendors')}
        icon={RotateCcw}
        actions={
          hasPermission('create_rtv') && (
            <Link to="/procurement/return-to-vendor/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('returnToVendor.createReturnToVendor')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('returnToVendor.searchPlaceholder'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={returnsToVendor}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/procurement/return-to-vendor/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('returnToVendor.noReturnToVendorsFound') : t('returnToVendor.noReturnToVendorsYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('returnToVendor.noReturnToVendorsFoundDescription')
            : t('returnToVendor.noReturnToVendorsYetDescription')
        }
        emptyIcon={<RotateCcw size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_rtv') ? (
            <Link to="/procurement/return-to-vendor/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('returnToVendor.createFirstReturnToVendor')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default ReturnToVendorListPage;
